import {ConstraintsBuilderInterface} from './constraints-builder-interface';
import {BoostStatementInterface, RuleItemInterface} from 'components/organisms/projection-boost-rule/interfaces';
import RuleBuilder from 'services/simplex/rule-builder';
import {addRoleSuffix, getSimplexDuplicateUnitName, getSimplexUnitName} from 'helpers/simplex-helper';
import {getPlayerUIdsByParams} from 'helpers/player-helper';
import {calcBoostedPoints, normalizePoints, isKeyPlayerLineupPositionUsed} from '../helpers';

interface Dependencies {
    readonly checkIfPlayerOnDoublePosition: boolean;
    readonly modes: string[];
    readonly playersIndexedById: object;
    readonly playersGroupedByTeamAndPosition: object;
    readonly projectionPointsType: string;
    readonly sportConfig: object;
}

class SimplexConstraintsBuilder implements ConstraintsBuilderInterface {
    private checkIfPlayerOnDoublePosition: boolean = false;
    private modes: string[] = [];
    private playersIndexedById: object = {};
    private playersGroupedByTeamAndPosition: object = {};
    private projectionPointsType: string = '';
    private sportConfig;

    public setDependencies(dependencies: Dependencies): void {
        const {
            checkIfPlayerOnDoublePosition,
            modes,
            playersIndexedById,
            playersGroupedByTeamAndPosition,
            projectionPointsType,
            sportConfig,
        } = dependencies;

        this.checkIfPlayerOnDoublePosition = checkIfPlayerOnDoublePosition;
        this.modes = modes;
        this.playersIndexedById = playersIndexedById;
        this.playersGroupedByTeamAndPosition = playersGroupedByTeamAndPosition;
        this.projectionPointsType = projectionPointsType;
        this.sportConfig = sportConfig;
    }

    public createRuleItemConstraints(ruleItem: RuleItemInterface): object {
        const result = {
            constraints: [],
            objective: {},
        };

        const useKeyPlayerLineupPosition = isKeyPlayerLineupPositionUsed(this.sportConfig);
        const unitNamesForTypeIncrease = {};

        this.getKeyPlayerIds(ruleItem).map((keyPlayerId) => {
            if (!(keyPlayerId in this.playersIndexedById)) {
                return;
            }

            const keyPlayer = this.playersIndexedById[keyPlayerId];

            if (!isEmpty(this.modes)) {
                const keyUnitNames = this.getSimplexUnitNamesByModes(
                    keyPlayerId,
                    useKeyPlayerLineupPosition ? [ruleItem.getKeyPlayerLineupPosition()] : this.modes
                );

                this.modes.map((mode) => {
                    ruleItem.getBoostStatements().map((boostStatement) => {
                        this.getBoostedPlayerIds(boostStatement, keyPlayer).map((playerId) => {
                            const player = this.playersIndexedById?.[playerId];

                            if (!player || !(this.projectionPointsType in player)) {
                                return;
                            }

                            const [boostedUnitName] = this.getSimplexUnitNamesByModes(playerId, [mode]);
                            const duplicateUnitName = addRoleSuffix({
                                prefix: getSimplexDuplicateUnitName([boostStatement.getId(), playerId]),
                                role: mode,
                            });

                            const unitPoints = normalizePoints(
                                player.getPropValueOfMode(this.projectionPointsType, mode),
                            );
                            result.objective[duplicateUnitName] = calcBoostedPoints(unitPoints, boostStatement);

                            if (boostStatement.isBoostTypeIncrease()) {
                                unitNamesForTypeIncrease[duplicateUnitName] ??= {
                                    keyUnitNames: [],
                                    boostedUnitNames: [],
                                };

                                keyUnitNames.map((unitName) =>
                                    unitNamesForTypeIncrease[duplicateUnitName].keyUnitNames.push(unitName),
                                );
                                unitNamesForTypeIncrease[duplicateUnitName].boostedUnitNames.push(boostedUnitName);
                            } else if (boostStatement.isBoostTypeDecrease()) {
                                const constraint = this.createDecreaseTypeConstraint(duplicateUnitName, keyUnitNames, [
                                    boostedUnitName,
                                ]);
                                // @ts-ignore
                                result.constraints.push(constraint);
                            }
                        });
                    });
                });
            } else {
                const keyUnitNames = this.getSimplexKeyUnitNames(keyPlayerId, ruleItem);

                ruleItem.getBoostStatements().map((boostStatement) => {
                    this.getBoostedPlayerIds(boostStatement, keyPlayer).map((playerId) => {
                        const player = this.playersIndexedById[playerId];

                        if (!player || !(this.projectionPointsType in player)) {
                            return;
                        }

                        const boostedUnitNames = this.getSimplexBoostedUnitNames(playerId, boostStatement);
                        const duplicateUnitName = getSimplexDuplicateUnitName([boostStatement.getId(), playerId]);

                        const unitPoints = normalizePoints(player[this.projectionPointsType]);
                        result.objective[duplicateUnitName] = calcBoostedPoints(unitPoints, boostStatement);

                        if (boostStatement.isBoostTypeIncrease()) {
                            unitNamesForTypeIncrease[duplicateUnitName] ??= {
                                keyUnitNames: [],
                                boostedUnitNames: [],
                            };

                            keyUnitNames.map((unitName) =>
                                unitNamesForTypeIncrease[duplicateUnitName].keyUnitNames.push(unitName),
                            );
                            boostedUnitNames.map((unitName) =>
                                unitNamesForTypeIncrease[duplicateUnitName].boostedUnitNames.push(unitName),
                            );
                        } else if (boostStatement.isBoostTypeDecrease()) {
                            const constraint = this.createDecreaseTypeConstraint(
                                duplicateUnitName,
                                keyUnitNames,
                                boostedUnitNames,
                            );
                            // @ts-ignore
                            result.constraints.push(constraint);
                        }
                    });
                });
            }
        });

        Object.keys(unitNamesForTypeIncrease).map((duplicateUnitName) => {
            const {keyUnitNames, boostedUnitNames} = unitNamesForTypeIncrease[duplicateUnitName];

            if (isEmpty(keyUnitNames) || isEmpty(boostedUnitNames)) {
                return;
            }

            const atMostRuleBuilder = RuleBuilder.fromAtMost(1);
            keyUnitNames.map((unitName) => atMostRuleBuilder.addVar(unitName, 1));
            boostedUnitNames.map((unitName) => atMostRuleBuilder.addVar(unitName, -1));
            atMostRuleBuilder.addVar(duplicateUnitName, 1);
            // @ts-ignore
            result.constraints.push(atMostRuleBuilder.createRule());

            const atLeastRuleBuilder = RuleBuilder.fromAtLeast(0);
            keyUnitNames.map((unitName) => atLeastRuleBuilder.addVar(unitName, 1));
            atLeastRuleBuilder.addVar(duplicateUnitName, -1);
            // @ts-ignore
            result.constraints.push(atLeastRuleBuilder.createRule());

            // NOTE! Constraints like "+1keyPlayer+1boostedPlayer-2duplicateBoostedPlayer>=0" works slower than
            // "+1keyPlayer+1boostedPlayer-1duplicateBoostedPlayer<=1; +1keyPlayer-1duplicateBoostedPlayer>=0"
            // const ruleBuilder = RuleBuilder.fromAtLeast(0);
            // unitNames.map(unitName => ruleBuilder.addVar(unitName, 1));
            // ruleBuilder.addVar(duplicateUnitName, -2);
            // // @ts-ignore
            // result.constraints.push(ruleBuilder.createRule());
        });

        return result;
    }

    private createDecreaseTypeConstraint(
        duplicateUnitName: string,
        keyUnitNames: string[],
        boostedUnitNames: string[],
    ): object {
        const ruleBuilder = RuleBuilder.fromAtMost(1);
        keyUnitNames.map((unitName) => ruleBuilder.addVar(unitName, 1));
        boostedUnitNames.map((unitName) => ruleBuilder.addVar(unitName, 1));
        ruleBuilder.addVar(duplicateUnitName, -1);

        return ruleBuilder.createRule();
    }

    private getKeyPlayerIds(ruleItem: RuleItemInterface): string[] {
        if (ruleItem.isStatementTypePlayer()) {
            return [ruleItem.getKeyPlayer()];
        } else if (ruleItem.isStatementTypePosition()) {
            const positionName = ruleItem.getKeyPosition();

            const playerIds = [];
            Object.values(this.playersGroupedByTeamAndPosition).map((team) => {
                // @ts-ignore
                (team.positions[positionName] ?? []).map((player) => playerIds.push(player.playerId));
            });

            return playerIds;
        }

        return [];
    }

    private getBoostedPlayerIds(boostStatement: BoostStatementInterface, keyPlayer): string[] {
        if (boostStatement.isBoostForPlayer()) {
            return [boostStatement.getBoostedPlayer()];
        } else if (boostStatement.isBoostForPosition()) {
            const team = this.getTeamPlayersGroupedByPosition(boostStatement, keyPlayer);

            const playerIds = [];
            boostStatement.getBoostedPositions().map((positionName) => {
                (team[positionName] ?? []).map((player) => {
                    // @ts-ignore
                    player.playerId !== keyPlayer.playerId && playerIds.push(player.playerId);
                });
            });

            return playerIds;
        }

        return [];
    }

    private getTeamPlayersGroupedByPosition(boostStatement: BoostStatementInterface, keyPlayer): object {
        if (boostStatement.isSameTeam()) {
            if (keyPlayer.team in this.playersGroupedByTeamAndPosition) {
                return this.playersGroupedByTeamAndPosition[keyPlayer.team].positions;
            }
        } else {
            if (keyPlayer.opponent in this.playersGroupedByTeamAndPosition) {
                return this.playersGroupedByTeamAndPosition[keyPlayer.opponent].positions;
            }
        }

        return {};
    }

    private getSimplexKeyUnitNames(playerId: string, ruleItem: RuleItemInterface): string[] {
        const positionsOnly: number[] = ruleItem.isStatementTypePosition()
            ? this.getPositionNumbersByNames([ruleItem.getKeyPosition()])
            : [];

        return getPlayerUIdsByParams({
            playerId,
            playersIndexedById: this.playersIndexedById,
            checkIfPlayerOnDoublePosition: this.checkIfPlayerOnDoublePosition,
            positionsOnly,
        }).map((playerId) => getSimplexUnitName(playerId));
    }

    private getSimplexBoostedUnitNames(playerId: string, boostStatement: BoostStatementInterface): string[] {
        const positionsOnly: number[] = boostStatement.isBoostForPosition()
            ? this.getPositionNumbersByNames(boostStatement.getBoostedPositions())
            : [];

        return getPlayerUIdsByParams({
            playerId,
            playersIndexedById: this.playersIndexedById,
            checkIfPlayerOnDoublePosition: this.checkIfPlayerOnDoublePosition,
            positionsOnly,
        }).map((playerId) => getSimplexUnitName(playerId));
    }

    private getSimplexUnitNamesByModes(playerId: string, modes: string[]): string[] {
        const prefix = getSimplexUnitName(playerId);

        return modes.map((mode) => addRoleSuffix({prefix, role: mode}));
    }

    private getPositionNumbersByNames(positionNames: string[]): number[] {
        const result: number[] = [];

        positionNames.map((positionName) => {
            const positionNumber = this.sportConfig.getPositionNumberByName(positionName);
            positionNumber !== null && result.push(Number(positionNumber));
        });

        return result;
    }
}

export {SimplexConstraintsBuilder};
